import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { FALLBACK_REVIEWS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <span className="flex gap-1" aria-label={`${n} stelle`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < n ? "fill-gold text-gold" : "text-sage/20"}`} />
    ))}
  </span>
);

export const Testimonials = () => {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => r.data?.length && setReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => go(1), 7000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews.length]);

  const go = (d) => {
    setDir(d);
    setI((v) => (v + d + reviews.length) % reviews.length);
  };
  const r = reviews[i];

  return (
    <section id="recensioni" className="bg-beige py-24 md:py-36" data-testid="reviews-section">
      <div className="container-x">
        <SectionHeading id="reviews" align="center" eyebrow="Recensioni" title={<>Le parole di chi ci <em className="font-normal italic text-sage">sceglie</em>.</>} />

        <Reveal delay={0.15} className="relative mx-auto mt-16 max-w-3xl">
          <Quote className="absolute -top-8 left-1/2 h-16 w-16 -translate-x-1/2 fill-gold/15 text-gold/15" />
          <div className="relative min-h-[300px] overflow-hidden rounded-[2rem] bg-white px-7 py-12 text-center shadow-[0_30px_70px_-40px_rgba(27,41,33,0.35)] md:px-16">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={r.id}
                initial={{ opacity: 0, x: 40 * dir }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 * dir }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid="review-card"
              >
                <div className="flex justify-center"><Stars n={r.rating} /></div>
                <p className="mt-6 font-serif text-2xl leading-snug text-ink md:text-3xl" data-testid="review-text">"{r.text}"</p>
                <div className="mt-8">
                  <div className="font-semibold text-ink" data-testid="review-name">{r.name}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.2em] text-muted">
                    {r.location}{r.service ? ` · ${r.service}` : ""}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6">
            <button onClick={() => go(-1)} className="rounded-full border border-sage/20 p-3 text-sage transition-colors hover:bg-sage hover:text-white" aria-label="Recensione precedente" data-testid="review-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex gap-2" data-testid="review-dots">
              {reviews.map((rv, k) => (
                <button
                  key={rv.id}
                  onClick={() => { setDir(k > i ? 1 : -1); setI(k); }}
                  aria-label={`Vai alla recensione ${k + 1}`}
                  className={`h-2 rounded-full transition-[width,background-color] duration-400 ${k === i ? "w-8 bg-gold" : "w-2 bg-sage/25"}`}
                />
              ))}
            </div>
            <button onClick={() => go(1)} className="rounded-full border border-sage/20 p-3 text-sage transition-colors hover:bg-sage hover:text-white" aria-label="Recensione successiva" data-testid="review-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
